import { isRecord, json, request, requestNoContent, requireString } from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import type { Replace } from '@/lib/type-utils'
import { canonicalizeDataGridFilters, decodeDataGridFilters } from '../lib/url-state'
import type { DataGridFilter, DataGridSort } from '../lib/types'

type SavedViewWire = components['schemas']['SavedViewResponse']
type CreateSavedViewWire = components['schemas']['CreateSavedViewRequest']
type UpdateSavedViewWire = components['schemas']['UpdateSavedViewRequest']
type OverwriteSavedViewWire = components['schemas']['OverwriteSavedViewRequest']

/** The rest of the table a saved view restores. An absent part means the grid's default. */
export type DataGridSavedViewState = {
  columns?: readonly string[]
  sort?: readonly DataGridSort[]
  search?: { query: string; columns?: readonly string[] }
  /** Values keyed by parameter ID, each checked against its declared type when the view is applied. */
  parameters?: Readonly<Record<string, unknown>>
}

export type DataGridSavedView = Replace<SavedViewWire, 'filters' | 'state', {
  filters: DataGridFilter[]
  /** Null for a filters-only view, which restores its filters and leaves the rest of the table alone. */
  state: DataGridSavedViewState | null
}>

type CreateDataGridSavedView = Replace<CreateSavedViewWire, 'name' | 'filters' | 'state', {
  name: string
  filters: DataGridFilter[]
  state?: DataGridSavedViewState
}>

type OverwriteDataGridSavedView = Replace<OverwriteSavedViewWire, 'filters' | 'state', {
  filters: DataGridFilter[]
  state: DataGridSavedViewState
}>

type UpdateDataGridSavedView =
  | { name: NonNullable<UpdateSavedViewWire['name']> }
  | { isDefault: NonNullable<UpdateSavedViewWire['isDefault']> }

function decodeSavedView(raw: unknown): DataGridSavedView {
  if (!isRecord(raw)) throw new Error('Malformed saved table view')
  if (!Array.isArray(raw.filters)) throw new Error('Malformed saved table view filters')
  return {
    savedViewId: requireString(raw.savedViewId, 'savedViewId'),
    name: requireString(raw.name, 'name'),
    filters: canonicalizeDataGridFilters(decodeDataGridFilters(raw.filters)),
    createdAt: requireString(raw.createdAt, 'createdAt'),
    state: decodeSavedViewState(raw.state),
    isDefault: raw.isDefault === true,
  }
}

function decodeSavedViewState(raw: unknown): DataGridSavedViewState | null {
  if (raw === null || raw === undefined) return null
  if (!isRecord(raw)) throw new Error('Malformed saved table view state')
  return {
    ...(raw.columns == null ? {} : { columns: stringList(raw.columns, 'columns') }),
    ...(raw.sort == null ? {} : { sort: list(raw.sort, 'sort').map(decodeSort) }),
    ...(raw.search == null ? {} : { search: decodeSearch(raw.search) }),
    ...(raw.parameters == null ? {} : { parameters: decodeParameters(raw.parameters) }),
  }
}

function decodeSort(raw: unknown): DataGridSort {
  if (!isRecord(raw) || (raw.direction !== 'asc' && raw.direction !== 'desc')) {
    throw new Error('Malformed saved table view sort')
  }
  return { column: requireString(raw.column, 'sort column'), direction: raw.direction }
}

function decodeSearch(raw: unknown): NonNullable<DataGridSavedViewState['search']> {
  if (!isRecord(raw)) throw new Error('Malformed saved table view search')
  const query = requireString(raw.query, 'search query')
  return raw.columns == null ? { query } : { query, columns: stringList(raw.columns, 'search columns') }
}

function decodeParameters(raw: unknown): Record<string, unknown> {
  if (!isRecord(raw)) throw new Error('Malformed saved table view parameters')
  return raw
}

function list(raw: unknown, field: string): unknown[] {
  if (!Array.isArray(raw)) throw new Error(`Malformed saved table view ${field}`)
  return raw
}

function stringList(raw: unknown, field: string): string[] {
  return list(raw, field).map((value) => requireString(value, field))
}

export const dataGridSavedViewsApi = {
  list: (gridId: string) => request(
    `/api/v1/data-grids/${encodeURIComponent(gridId)}/saved-views`,
    (raw) => {
      if (!Array.isArray(raw)) throw new Error('Malformed saved table view list')
      return raw.map(decodeSavedView)
    },
  ),
  create: (gridId: string, input: CreateDataGridSavedView) => request(
    `/api/v1/data-grids/${encodeURIComponent(gridId)}/saved-views`,
    decodeSavedView,
    json('POST', input),
  ),
  update: (gridId: string, savedViewId: string, input: UpdateDataGridSavedView) => requestNoContent(
    `/api/v1/data-grids/${encodeURIComponent(gridId)}/saved-views/${encodeURIComponent(savedViewId)}`,
    json('PATCH', input),
  ),
  overwrite: (gridId: string, savedViewId: string, input: OverwriteDataGridSavedView) => request(
    `/api/v1/data-grids/${encodeURIComponent(gridId)}/saved-views/${encodeURIComponent(savedViewId)}/table`,
    decodeSavedView,
    json('PUT', input),
  ),
  delete: (gridId: string, savedViewId: string) => requestNoContent(
    `/api/v1/data-grids/${encodeURIComponent(gridId)}/saved-views/${encodeURIComponent(savedViewId)}`,
    { method: 'DELETE' },
  ),
}
